import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Server, Key, Wallet, Coins, AlertTriangle, CheckCircle2, Loader2, Download, Copy, RefreshCw, Play, Square } from 'lucide-react';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppMode } from '@/contexts/ModeContext';
import { useMintSetup } from '@/hooks/useMintSetup';
import { useMintListenerContext } from '@/contexts/MintListenerContext';
import { useNodeStatus } from '@/hooks/useNodeStatus';
import { StatusBar } from '@/components/shared/StatusBar';
import { WarningBanner } from '@/components/shared/WarningBanner';
import { useToast } from '@/hooks/useToast';
import { nip19 } from 'nostr-tools';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/storage/db';
import { DEFAULT_NODES } from '@/lib/monero/nodes';

/** Mint Setup Wizard */
export function MintSetupWizard() {
  const [step, setStep] = useState<'nostr' | 'monero' | 'config' | 'done'>('nostr');
  const [nostrIdentity, setNostrIdentity] = useState<{ nsecHex: string; npubHex: string; nsecBech32: string } | null>(null);
  const [moneroWallet, setMoneroWallet] = useState<{ seed: string; address: string; viewKey: string; spendKey: string } | null>(null);
  const [name, setName] = useState('My Cashero Mint');
  const [description, setDescription] = useState('');
  const [nodeUri, setNodeUri] = useState(DEFAULT_NODES[0].host + ':' + DEFAULT_NODES[0].port);
  const [depositFee, setDepositFee] = useState('0.01');
  const [withdrawFee, setWithdrawFee] = useState('0.00001');
  const [confirmations, setConfirmations] = useState('1');
  const [generated, setGenerated] = useState(false);
  const [nsaved, setNsaved] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    generateNostrIdentity,
    generateMoneroWallet,
    finalizeSetup,
    loading,
    setupState,
  } = useMintSetup();

  /** Step 1: Generate Nostr identity */
  const handleGenerateNostr = () => {
    try {
      const identity = generateNostrIdentity();
      setNostrIdentity(identity);
      setGenerated(true);
    } catch (err) {
      toast({ title: 'Failed to generate identity', description: (err as Error).message, variant: 'destructive' });
    }
  };

  /** Step 2: Create Monero wallet */
  const handleCreateMonero = async () => {
    setCreating(true);
    try {
      const wallet = await generateMoneroWallet(`http://${nodeUri}`);
      setMoneroWallet(wallet);
      setStep('config');
    } catch (err) {
      toast({ title: 'Failed to create Monero wallet', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  /** Step 3: Finalize */
  const handleFinalize = async () => {
    if (!nostrIdentity || !moneroWallet) return;
    try {
      await finalizeSetup(
        name,
        description,
        nostrIdentity.nsecHex,
        nostrIdentity.npubHex,
        moneroWallet.seed,
        moneroWallet.address,
        moneroWallet.viewKey,
        moneroWallet.spendKey,
        `http://${nodeUri}`,
        parseFloat(depositFee),
        parseInt(withdrawFee, 10),
        parseInt(confirmations, 10),
        0,
      );
      setStep('done');
      toast({ title: 'Mint created!', description: 'Your Cashero mint is ready.' });
    } catch (err) {
      toast({ title: 'Setup failed', description: (err as Error).message, variant: 'destructive' });
    }
  };

  const downloadNsec = () => {
    if (!nostrIdentity) return;
    const blob = new Blob([nostrIdentity.nsecBech32], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cashero-mint-nsec.txt';
    a.click();
    URL.revokeObjectURL(url);
    setNsaved(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: 'Copied to clipboard.' });
  };

  // Derive npub from nostrIdentity or try loading from stored config
  const npubBech32 = (() => {
    try {
      const hex = nostrIdentity?.npubHex;
      if (!hex) return '';
      return nip19.npubEncode(hex);
    } catch {
      return '';
    }
  })();

  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <CardTitle>Mint is Ready!</CardTitle>
            <CardDescription>
              Your Cashero mint is now live on Nostr. Wallets can find it by searching for your mint&apos;s npub.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">Mint npub (share this)</Label>
              <div className="flex gap-2">
                <Input
                  value={npubBech32}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button size="icon" variant="outline" onClick={() => {
                  if (npubBech32) copyToClipboard(npubBech32);
                }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button onClick={() => { window.location.href = '/mint'; }} className="w-full">
              Go to Mint Dashboard
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Keep your nsec safe. If you lose it, the mint is permanently inaccessible.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <h1 className="text-lg font-bold">Set Up Your Cashero Mint</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <WarningBanner message="This is experimental software. Start with small amounts you can afford to lose." />

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-sm">
          {['nostr', 'monero', 'config'].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <Badge variant={step === s ? 'default' : 'outline'}>
                {i + 1}
              </Badge>
              <span className={step === s ? '' : 'text-muted-foreground'}>
                {s === 'nostr' ? 'Identity' : s === 'monero' ? 'Wallet' : 'Settings'}
              </span>
              {i < 2 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        {step === 'nostr' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Step 1: Generate Mint Identity
              </CardTitle>
              <CardDescription>
                Your mint needs a Nostr identity (nsec) to communicate with wallets and publish its announcement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!generated ? (
                <Button onClick={handleGenerateNostr}>
                  <Loader2 className="h-4 w-4 mr-2" />
                  Generate Mint nsec
                </Button>
              ) : nostrIdentity && (
                <div className="space-y-4">
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg space-y-2">
                    <p className="text-sm font-semibold text-destructive flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Save this key — it cannot be recovered!
                    </p>
                    <p className="text-xs text-muted-foreground">
                      This nsec is your mint's identity. If you lose it, all issued ecash
                      becomes permanently unredeemable and you cannot operate the mint.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Mint nsec (your private key)</Label>
                    <div className="flex gap-2">
                      <Input value={nostrIdentity.nsecBech32} readOnly className="font-mono text-xs" />
                      <Button size="icon" variant="outline" onClick={() => copyToClipboard(nostrIdentity.nsecBech32)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Mint npub (public key — share this)</Label>
                    <Input value={nostrIdentity.npubHex} readOnly className="font-mono text-xs" />
                  </div>
                  <Button onClick={downloadNsec} variant="secondary" className="w-full">
                    <Download className="h-4 w-4 mr-2" />
                    Download nsec as .txt file
                  </Button>
                  <Button
                    onClick={() => setStep('monero')}
                    className="w-full"
                    disabled={!nsaved}
                  >
                    {!nsaved ? 'Download nsec to continue' : 'Continue'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {step === 'monero' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Step 2: Create Monero Wallet
              </CardTitle>
              <CardDescription>
                A new Monero wallet will be created in your browser. No sync needed for a new wallet.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Monero Node</Label>
                <Input value={nodeUri} onChange={(e) => setNodeUri(e.target.value)} placeholder="host:port" />
                <p className="text-xs text-muted-foreground">
                  Default: {DEFAULT_NODES.map(n => `${n.host}:${n.port}`).join(', ')}
                </p>
              </div>
              <Button onClick={handleCreateMonero} disabled={loading || creating}>
                {creating ? 'Loading WASM (~1MB)...' : loading ? 'Creating...' : 'Create Monero Wallet'}
              </Button>
              {moneroWallet && (
                <div className="p-3 bg-muted rounded-lg space-y-2 text-xs">
                  <p><strong>Address:</strong> {moneroWallet.address}</p>
                  <p><strong>Seed:</strong> {moneroWallet.seed.slice(0, 30)}...</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {step === 'config' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5" />
                Step 3: Mint Settings
              </CardTitle>
              <CardDescription>Configure your mint's name, fees, and keyset.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Mint Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description of your mint" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Deposit Fee (%)</Label>
                  <Input value={depositFee} onChange={(e) => setDepositFee(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Withdraw Fee (piconero)</Label>
                  <Input value={withdrawFee} onChange={(e) => setWithdrawFee(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Min Confirmations</Label>
                  <Input value={confirmations} onChange={(e) => setConfirmations(e.target.value)} />
                </div>
              </div>
              <Button onClick={handleFinalize} disabled={loading} className="w-full">
                {loading ? 'Publishing...' : 'Publish Mint & Finish Setup'}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

/** Mint Dashboard (after setup) */
export function MintDashboard() {
  const { listening, startListening, stopListening } = useMintListenerContext();
  const mintConfig = useLiveQuery(() => db.mintConfig.get('config'));
  const { status: nodeStatus, height: nodeHeight } = useNodeStatus(mintConfig?.moneroNodeUri ?? null);
  const keysets = useLiveQuery(() => db.mintKeysets.toArray());
  const deposits = useLiveQuery(() => db.pendingDeposits.toArray());
  const { setMode } = useAppMode();

  const pendingCount = deposits?.filter(d => d.status === 'waiting' || d.status === 'incoming').length ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold">Cashero Mint</h1>
            <Badge variant="outline" className="text-[10px]">Operator</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setMode('wallet')}>
              Switch to Wallet
            </Button>
            <Button
              size="sm"
              variant={listening ? 'destructive' : 'default'}
              onClick={listening ? stopListening : startListening}
            >
              {listening ? <Square className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
              {listening ? 'Stop' : 'Start'}
            </Button>
          </div>
        </div>
      </header>
      <StatusBar showMintStatus mintListening={listening} moneroNodeUri={mintConfig?.moneroNodeUri ?? null} />

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <WarningBanner />

        {mintConfig && (
          <Card className="border-yellow-500/50 bg-yellow-500/5">
            <CardContent className="py-2 text-xs text-yellow-700 dark:text-yellow-400">
              Keep this tab open for the mint to stay live. Closing it stops all deposit, swap, and withdrawal processing.
            </CardContent>
          </Card>
        )}

        {mintConfig && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Mint Info</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <p className="text-xs text-muted-foreground">Mint npub</p>
                <div className="flex gap-1 mt-1">
                  <p className="text-xs font-mono truncate">
                    {(() => {
                      try { return nip19.npubEncode(mintConfig.npub); }
                      catch { return mintConfig.npub.slice(0, 12) + '...'; }
                    })()}
                  </p>
                  <button
                    onClick={() => {
                      try {
                        const npub = nip19.npubEncode(mintConfig.npub);
                        navigator.clipboard.writeText(npub);
                      } catch { navigator.clipboard.writeText(mintConfig.npub); }
                    }}
                    className="shrink-0"
                  >
                    <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Deposit Fee</p>
                <p className="text-sm font-medium">{mintConfig.depositFeePercent}%</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Withdraw Fee</p>
                <p className="text-sm font-medium">{mintConfig.withdrawFeeFlat} piconero</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Confirmations</p>
                <p className="text-sm font-medium">&ge;{mintConfig.requiredConfirmations}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Keysets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Key className="h-4 w-4" />
                Keysets
              </CardTitle>
            </CardHeader>
            <CardContent>
              {keysets && keysets.length > 0 ? (
                <div className="space-y-2">
                  {keysets.map((ks) => (
                    <div key={ks.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <div>
                        <p className="text-xs font-mono">{ks.id.slice(0, 16)}...</p>
                        <p className="text-[10px] text-muted-foreground">
                          {Object.keys(ks.pubkeys).length} denominations
                        </p>
                      </div>
                      <Badge variant={ks.active ? 'default' : 'secondary'}>
                        {ks.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No keysets</p>
              )}
            </CardContent>
          </Card>

          {/* Pending Deposits */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Coins className="h-4 w-4" />
                Pending Deposits
                {pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-auto">{pendingCount}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deposits && deposits.length > 0 ? (
                <div className="space-y-2">
                  {deposits.slice(0, 10).map((d) => (
                    <div key={d.requestId} className="flex items-center justify-between p-2 rounded bg-muted/50 text-xs">
                      <div>
                        <p className="font-mono">{d.requestId.slice(0, 8)}...</p>
                        <p className="text-muted-foreground">
                          {d.expectedAmount.toLocaleString()} piconero · Sub #{d.subaddressIndex}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={
                          d.status === 'waiting' ? 'outline' :
                          d.status === 'incoming' ? 'secondary' :
                          d.status === 'confirmed' ? 'default' : 'default'
                        } className="text-[10px]">
                          {d.status}
                        </Badge>
                        {d.txHash && <p className="text-[10px] text-muted-foreground mt-1">{d.confirmations} conf</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No pending deposits</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Spent secrets count */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Double-Spend Protection</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Tracking spent secrets via local IndexedDB and Nostr spent-proof events.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
